import { useState, useRef } from 'react';
import { Text, Image, Settings, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

export default function TshirtCustomizer() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('');
  const [color, setColor] = useState('white');
  const [selectedView, setSelectedView] = useState('front');
  const [customText, setCustomText] = useState('');
  const [textPosition, setTextPosition] = useState({ x: 50, y: 40 });
  const [textColor, setTextColor] = useState('black');
  const [textSize, setTextSize] = useState('medium');
  const [textFont, setTextFont] = useState('Arial');
  const [showDesignPanel, setShowDesignPanel] = useState(false);
  const [uploadedDesign, setUploadedDesign] = useState(null);
  const fileInputRef = useRef(null);

  const productViews = {
    'front': "/api/placeholder/300/380",
    'back': "/api/placeholder/300/380",
    'sleeve': "/api/placeholder/300/380",
    'closeup': "/api/placeholder/300/380"
  };

  const relatedProducts = [
    { id: 1, name: "Gildan Softstyle Jersey T-shirt", image: "/api/placeholder/150/180", type: "hoodie" },
    { id: 2, name: "Gildan Softstyle Jersey T-shirt", image: "/api/placeholder/150/180", type: "women" },
    { id: 3, name: "Gildan Softstyle Jersey T-shirt", image: "/api/placeholder/150/180", type: "youth" },
    { id: 4, name: "Gildan Softstyle Jersey T-shirt", image: "/api/placeholder/150/180", type: "women" }
  ];

  const handleTabClick = (tab) => {
    if (activeTab === tab) {
      setActiveTab('');
      setShowDesignPanel(false);
    } else {
      setActiveTab(tab);
      setShowDesignPanel(true);
    }
  };

  const fontOptions = ['Arial', 'Times New Roman', 'Comic Sans MS', 'Verdana'];
  const fontSizes = ['small', 'medium', 'large', 'x-large'];
  const colorOptions = ['black', 'white', 'red', 'blue', 'green', 'yellow'];
  const productColors = ['white', 'black', 'navy', 'gray', 'red'];

  // Define tshirt images mapping
  const tshirtImages = {
    white: '/images/tshirts/white.png',
    black: '/images/tshirts/black.png',
    red: '/images/tshirts/red.png',
    blue: '/images/tshirts/blue.png',
    green: '/images/tshirts/green.png'
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100 p-5">
      {/* Main product display area */}
      <div className="flex bg-gray-200">
        {/* Left sidebar thumbnails */}
        <div className="w-24 bg-white border-r">
          {Object.entries(productViews).map(([view, url]) => (
            <div 
              key={view} 
              className={`cursor-pointer p-2 ${selectedView === view ? 'border-2 border-purple-500' : 'border border-gray-200'} m-2 rounded-md`}
              onClick={() => setSelectedView(view)}
            >
              <img 
                src={url} 
                alt={`${view} view`} 
                className="w-full"
              />
              <div className="text-xs text-center mt-1 capitalize">{view}</div>
            </div>
          ))}
        </div>

        {/* Main product and customization area */}
        <div className="flex-1 flex">
          {/* T-shirt display */}
          <div className="flex-1 flex items-center justify-center p-4 relative">
            <div className="relative">
              <img 
                src={tshirtImages[color]} 
                alt="T-shirt preview" 
                className="max-h-96"
              />
              {customText && selectedView === 'front' && (
                <div 
                  className="absolute" 
                  style={{ 
                    top: `${textPosition.y}%`, 
                    left: `${textPosition.x}%`, 
                    transform: 'translate(-50%, -50%)',
                    color: textColor,
                    fontSize: textSize === 'small' ? '14px' : textSize === 'medium' ? '18px' : textSize === 'large' ? '24px' : '32px',
                    fontFamily: textFont,
                    cursor: 'move'
                  }}
                >
                  {customText}
                </div>
              )}
            </div>
          </div>

          {/* Right side customization panel */}
          <div className="w-80 border-l flex flex-col bg-white">
            {/* What do you want to start? header */}
            <div className="p-4 border-b">
              <h2 className="text-lg font-medium">What do you want to start?</h2>
            </div>

            {/* Design panel content */}
            <div className="flex-1 flex flex-col">
              <div className="flex-1 p-4">
                {!showDesignPanel && (
                  <div className="flex items-center justify-center h-full">
                    <Image size={40} className="text-gray-300" />
                  </div>
                )}

                {/* Add Text Panel */}
                {activeTab === 'addText' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Your Text</label>
                      <input
                        type="text"
                        value={customText}
                        onChange={(e) => setCustomText(e.target.value)}
                        placeholder="Enter your text here"
                        className="w-full p-2 border rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Font</label>
                      <select 
                        value={textFont} 
                        onChange={(e) => setTextFont(e.target.value)}
                        className="w-full p-2 border rounded"
                      >
                        {fontOptions.map(font => (
                          <option key={font} value={font}>{font}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Size</label>
                      <select 
                        value={textSize} 
                        onChange={(e) => setTextSize(e.target.value)}
                        className="w-full p-2 border rounded"
                      >
                        {fontSizes.map(size => (
                          <option key={size} value={size}>{size.charAt(0).toUpperCase() + size.slice(1)}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Text Color</label>
                      <div className="flex flex-wrap gap-2">
                        {colorOptions.map(clr => (
                          <button
                            key={clr}
                            className={`w-8 h-8 rounded-full border ${textColor === clr ? 'border-2 border-purple-500' : 'border-gray-200'}`}
                            style={{ backgroundColor: clr }}
                            onClick={() => setTextColor(clr)}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Position</label>
                      <div className="flex gap-2">
                        <button 
                          className="px-3 py-1 border rounded"
                          onClick={() => setTextPosition({...textPosition, y: Math.max(textPosition.y - 5, 5)})}
                        >
                          Up
                        </button>
                        <button 
                          className="px-3 py-1 border rounded"
                          onClick={() => setTextPosition({...textPosition, y: Math.min(textPosition.y + 5, 95)})}
                        >
                          Down
                        </button>
                        <button 
                          className="px-3 py-1 border rounded"
                          onClick={() => setTextPosition({...textPosition, x: Math.max(textPosition.x - 5, 5)})}
                        >
                          Left
                        </button>
                        <button 
                          className="px-3 py-1 border rounded"
                          onClick={() => setTextPosition({...textPosition, x: Math.min(textPosition.x + 5, 95)})}
                        >
                          Right
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Add Design Panel */}
                {activeTab === 'addDesign' && (
                  <div className="space-y-4">
                    <h3 className="font-medium">Choose a Design</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {[1, 2, 3, 4, 5, 6].map(item => (
                        <div key={item} className="border rounded cursor-pointer p-2">
                          <img src="/api/placeholder/80/80" alt={`Design ${item}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Product Color Panel */}
                {activeTab === 'productColor' && (
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium mb-2">Product Color</h3>
                      <div className="flex flex-wrap gap-2">
                        {productColors.map(clr => (
                          <button
                            key={clr}
                            className={`w-10 h-10 rounded-full border ${color === clr ? 'border-2 border-purple-500' : 'border-gray-200'}`}
                            style={{ 
                              backgroundColor: 
                                clr === 'white' ? '#ffffff' : 
                                clr === 'black' ? '#000000' : 
                                clr === 'navy' ? '#001f3f' : 
                                clr === 'gray' ? '#808080' : '#ff0000' 
                            }}
                            onClick={() => setColor(clr)}
                          />
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-medium mb-2">Size</h3>
                      <div className="flex flex-wrap gap-2">
                        {['XS', 'S', 'M', 'L', 'XL', '2XL'].map(size => (
                          <button key={size} className="px-3 py-1 border rounded hover:bg-gray-100">
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Change Product Panel */}
                {activeTab === 'changeProduct' && (
                  <div className="space-y-4">
                    <h3 className="font-medium">Choose Product Type</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="border rounded cursor-pointer p-2">
                        <img src="/api/placeholder/80/100" alt="T-shirt" />
                        <div className="text-xs text-center mt-1">T-shirt</div>
                      </div>
                      <div className="border rounded cursor-pointer p-2">
                        <img src="/api/placeholder/80/100" alt="Hoodie" />
                        <div className="text-xs text-center mt-1">Hoodie</div>
                      </div>
                      <div className="border rounded cursor-pointer p-2">
                        <img src="/api/placeholder/80/100" alt="Tank Top" />
                        <div className="text-xs text-center mt-1">Tank Top</div>
                      </div>
                      <div className="border rounded cursor-pointer p-2">
                        <img src="/api/placeholder/80/100" alt="Long Sleeve" />
                        <div className="text-xs text-center mt-1">Long Sleeve</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom tabs - now directly below the "What do you want to start?" header */}
              <div className="bg-gray-900 text-white flex mt-auto">
                <button 
                  className={`flex-1 py-3 flex flex-col items-center ${activeTab === 'addText' ? 'bg-gray-800' : ''}`}
                  onClick={() => handleTabClick('addText')}
                >
                  <Text size={20} />
                  <span className="text-xs mt-1">Add Text</span>
                </button>
                <button 
                  className={`flex-1 py-3 flex flex-col items-center ${activeTab === 'addDesign' ? 'bg-gray-800' : ''}`}
                  onClick={() => handleTabClick('addDesign')}
                >
                  <Image size={20} />
                  <span className="text-xs mt-1">Add Design</span>
                </button>
                <button 
                  className={`flex-1 py-3 flex flex-col items-center ${activeTab === 'productColor' ? 'bg-gray-800' : ''}`}
                  onClick={() => handleTabClick('productColor')}
                >
                  <Settings size={20} />
                  <span className="text-xs mt-1">Product Color</span>
                </button>
                <button 
                  className={`flex-1 py-3 flex flex-col items-center ${activeTab === 'changeProduct' ? 'bg-gray-800' : ''}`}
                  onClick={() => handleTabClick('changeProduct')}
                >
                  <ShoppingBag size={20} />
                  <span className="text-xs mt-1">Change Product</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product selection area */}
      <div className="flex items-center justify-between p-4 bg-white border-t border-b">
        <div className="flex items-center gap-4">
          <img src="/api/placeholder/60/60" alt="Product thumbnail" className="border" />
          <div>
            <div className="text-sm text-gray-500">Gildan Softstyle Jersey T-shirt <span className="text-blue-500">View Product</span></div>
            <div className="flex items-center mt-1">
              <input 
                type="checkbox" 
                id="whiteColor" 
                className="mr-2" 
                checked={color === 'white'} 
                onChange={() => setColor('white')}
              />
              <label htmlFor="whiteColor" className="text-sm">White</label>
              <span className="text-sm text-blue-500 ml-4 cursor-pointer">Change Color</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="border border-purple-500 rounded px-4 py-2 text-purple-500 flex items-center gap-1">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
            Save | Share
          </button>
          <button className="bg-purple-500 text-white rounded px-4 py-2">Get Price</button>
        </div>
      </div>

      {/* Product details section */}
      <div className="bg-gray-50 p-4">
        <h2 className="text-xl font-medium mb-4">Gildan Softstyle Jersey T-shirt</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="md:col-span-1">
            <h3 className="font-medium mb-2">Product Description</h3>
          </div>
          <div className="md:col-span-3">
            <p className="text-sm mb-4">
              Trendy and budget-friendly - what's not to love about this style? Try this on for size: a lightweight and comfortable fabric with an exciting color palette, all at an affordable price. An excellent choice for your next event!
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-4">
          <div className="md:col-span-1">
            <h3 className="font-medium mb-2">More Detail</h3>
          </div>
          <div className="md:col-span-3">
            <ul className="text-sm space-y-2">
              <li>Screen printing or high-quality digital printing</li>
              <li>4.5 oz./yd² (153 g/m²)</li>
              <li>Sport Grey and Antique colors 60/40 cotton/polyester</li>
              <li>Graphite Heather: 50/50 cotton/polyester all other Heather colors 65/35 polyester/cotton</li>
              <li>Youth sizes: White, Sport Grey, Black, Navy, Royal, Red, and Daisy colors are 5.3</li>
              <li>Tear-away label; taped neck and shoulders</li>
              <li>Seamless double-needle 3/4" collar and bottom hem for durability</li>
              <li>Pre-shrunk (minimal shrinkage, machine-wash cold with like cotton items to prevent pilling)</li>
              <li>Made with OEKO-TEX certified low-impact dyes</li>
              <li>Note: There may be minor variations in colors of the product due to the nature of the fabric.</li>
              <li>Minimum Quantity: 1</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Frequently Bought Together */}
      <div className="bg-white p-4">
        <h2 className="text-xl font-medium mb-4">Frequently Bought Together</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {relatedProducts.map(product => (
            <div key={product.id} className="flex flex-col items-center">
              <img src={product.image} alt={product.name} className="mb-2" />
              <h3 className="text-center text-sm">Gildan Softstyle<br/>Jersey T-shirt</h3>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}